import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaUserMd, FaPlus, FaTimes, FaPencilAlt, FaTrash, FaChevronRight, FaCheckCircle } from 'react-icons/fa';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const DEPARTMENTS = [
  'Community Medicine', 'General Medicine', 'General Surgery', 'Obstetrics & Gynaecology',
  'Paediatrics', 'Orthopaedics', 'Anaesthesiology', 'Emergency Medicine / Casualty',
  'ENT', 'Ophthalmology', 'Dermatology & STD', 'Psychiatry', 'Forensic Medicine',
  'Radiodiagnosis', 'Elective', 'Other'
];

const INVOLVEMENT_LEVELS = ['Observed', 'Assisted', 'Performed'];
const INVOLVEMENT_BADGE_CLASS = { Observed: 'ug-reflection-type-weekly', Assisted: 'ug-reflection-type-monthly', Performed: 'ug-reflection-type-case' };

const emptyPostingForm = () => ({
  department: 'Community Medicine', fromDate: '', toDate: '',
  unitWardHospital: '', supervisorName: '', leaveDaysTaken: '', reflection: ''
});

const emptyActivityForm = () => ({
  date: new Date().toISOString().slice(0, 10), procedureOrCase: '', involvementLevel: 'Observed',
  caseSummary: '', learningPoints: ''
});

const formatDate = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const UGInternship = ({ onBack }) => {
  const [view, setView] = useState('postings'); // 'postings' | 'posting-detail'
  const [postings, setPostings] = useState([]);
  const [postingsLoading, setPostingsLoading] = useState(true);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const [postingModalEntry, setPostingModalEntry] = useState(null);
  const [postingForm, setPostingForm] = useState(emptyPostingForm());
  const [activityModalEntry, setActivityModalEntry] = useState(null);
  const [activityForm, setActivityForm] = useState(emptyActivityForm());
  const [submitting, setSubmitting] = useState(false);
  const [activityPage, setActivityPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchPostings = useCallback(async () => {
    try {
      setPostingsLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/internship/postings/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setPostings(data.postings || []);
    } catch (err) {
      console.error('Error fetching internship postings:', err);
    } finally {
      setPostingsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchPostings(); }, [fetchPostings]);

  const fetchActivities = useCallback(async (postingId) => {
    try {
      setActivitiesLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/internship/activities/mine?postingId=${postingId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setActivities(data.activities || []);
    } catch (err) {
      console.error('Error fetching internship activities:', err);
    } finally {
      setActivitiesLoading(false);
    }
  }, [authHeaders]);

  const openPosting = (posting) => {
    setSelectedPosting(posting);
    setView('posting-detail');
    setActivityPage(1);
    fetchActivities(posting._id);
  };

  const backToPostings = () => {
    setView('postings');
    setSelectedPosting(null);
    fetchPostings();
  };

  // ---- Posting CRUD ----
  const openNewPosting = () => { setPostingForm(emptyPostingForm()); setPostingModalEntry('new'); };
  const openEditPosting = (posting) => {
    setPostingForm({
      department: posting.department,
      fromDate: new Date(posting.fromDate).toISOString().slice(0, 10),
      toDate: new Date(posting.toDate).toISOString().slice(0, 10),
      unitWardHospital: posting.unitWardHospital || '',
      supervisorName: posting.supervisorName || '',
      leaveDaysTaken: posting.leaveDaysTaken || '',
      reflection: posting.reflection || ''
    });
    setPostingModalEntry(posting);
  };

  const submitPosting = async () => {
    if (!postingForm.department.trim() || !postingForm.fromDate || !postingForm.toDate) {
      alert('Please fill in Department, From date and To date before saving.');
      return;
    }
    const isEdit = postingModalEntry && postingModalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/internship/postings${isEdit ? `/${postingModalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...postingForm, leaveDaysTaken: postingForm.leaveDaysTaken === '' ? 0 : Number(postingForm.leaveDaysTaken) })
      });
      const data = await res.json();
      if (data.success) {
        setPostingModalEntry(null);
        await fetchPostings();
      } else {
        alert(data.message || 'Failed to save posting');
      }
    } catch (err) {
      console.error('Error saving internship posting:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const removePosting = async (posting) => {
    if (!window.confirm(`Delete the "${posting.department}" posting and all its logged activities? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/internship/postings/${posting._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchPostings();
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting internship posting:', err);
    }
  };

  // ---- Activity CRUD ----
  const openNewActivity = () => { setActivityForm(emptyActivityForm()); setActivityModalEntry('new'); };
  const openEditActivity = (activity) => {
    setActivityForm({
      date: new Date(activity.date).toISOString().slice(0, 10),
      procedureOrCase: activity.procedureOrCase,
      involvementLevel: activity.involvementLevel,
      caseSummary: activity.caseSummary || '',
      learningPoints: activity.learningPoints || ''
    });
    setActivityModalEntry(activity);
  };

  const submitActivity = async () => {
    if (!activityForm.procedureOrCase.trim()) {
      alert('Please fill in Procedure / Case before saving.');
      return;
    }
    const isEdit = activityModalEntry && activityModalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/internship/activities${isEdit ? `/${activityModalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...activityForm, postingId: selectedPosting._id })
      });
      const data = await res.json();
      if (data.success) {
        setActivityModalEntry(null);
        await fetchActivities(selectedPosting._id);
      } else {
        alert(data.message || 'Failed to save activity');
      }
    } catch (err) {
      console.error('Error saving internship activity:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const removeActivity = async (activity) => {
    if (!window.confirm('Delete this activity log entry? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/internship/activities/${activity._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchActivities(selectedPosting._id);
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting internship activity:', err);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // activity alone does not flip its review status back to pending.
  const resubmitActivity = async (activity) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/internship/activities/${activity._id}/resubmit`, { method: 'PUT', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchActivities(selectedPosting._id);
      else alert(data.message || 'Failed to resubmit');
    } catch (err) {
      console.error('Error resubmitting internship activity:', err);
    }
  };

  const totalLeaveDays = postings.reduce((sum, p) => sum + (p.leaveDaysTaken || 0), 0);

  if (view === 'posting-detail' && selectedPosting) {
    return (
      <main className="ug-main">
        <button className="ug-back-btn" onClick={backToPostings}><FaChevronLeft /> Back to Postings</button>
        <h2 className="ug-section-title"><FaUserMd /> {selectedPosting.department}</h2>
        <div className="ug-submission-meta" style={{ marginBottom: 14 }}>
          <span>{formatDate(selectedPosting.fromDate)} – {formatDate(selectedPosting.toDate)}</span>
          {selectedPosting.unitWardHospital && <span>Unit/Ward: {selectedPosting.unitWardHospital}</span>}
          {selectedPosting.supervisorName && <span>Supervisor: {selectedPosting.supervisorName}</span>}
        </div>

        <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNewActivity}>
          <FaPlus /> Log procedure / case
        </button>

        {activitiesLoading ? (
          <div className="ug-due-empty">Loading activity log...</div>
        ) : activities.length === 0 ? (
          <div className="ug-due-empty">No procedures or cases logged yet for this posting.</div>
        ) : (
          <>
          <div className="ug-reflection-list">
            {activities.slice((activityPage - 1) * ITEMS_PER_PAGE, activityPage * ITEMS_PER_PAGE).map(a => (
              <div key={a._id} className="ug-reflection-card">
                <div className="ug-reflection-card-top">
                  <span className={`ug-reflection-type-badge ${INVOLVEMENT_BADGE_CLASS[a.involvementLevel]}`}>{a.involvementLevel}</span>
                  <span className="ug-reflection-date">{formatDate(a.date)}</span>
                  {a.review?.status === 'approved' && <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>}
                  {a.review?.status === 'needs_revision' && <span className="ug-status-badge ug-status-revision">Needs revision</span>}
                </div>
                <h4 className="ug-reflection-period">{a.procedureOrCase}</h4>

                {a.caseSummary && (<><div className="ug-reflection-section-label">Case Summary</div><p className="ug-reflection-text">{a.caseSummary}</p></>)}
                {a.learningPoints && (<><div className="ug-reflection-section-label">Learning Points</div><p className="ug-reflection-text">{a.learningPoints}</p></>)}
                {(a.review?.feedback || a.facultyFeedback) && (<><div className="ug-reflection-section-label">Faculty Feedback</div><p className="ug-reflection-text ug-reflection-faculty-feedback">{a.review?.feedback || a.facultyFeedback}</p></>)}
                {a.review?.reviewerName && (
                  <p className="ug-reviewer-tag">
                    {a.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {a.review.reviewerName}
                  </p>
                )}

                <div className="ug-reflection-card-actions">
                  {a.review?.status !== 'approved' && (
                    <>
                      <button className="ug-reflection-icon-btn" onClick={() => openEditActivity(a)} title="Edit"><FaPencilAlt /></button>
                      <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => removeActivity(a)} title="Delete"><FaTrash /></button>
                    </>
                  )}
                  {a.review?.status === 'needs_revision' && (
                    <button className="ug-log-btn" onClick={() => resubmitActivity(a)}>Resubmit for review</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination currentPage={activityPage} totalCount={activities.length} onPageChange={setActivityPage} />
          </>
        )}

        {activityModalEntry && (
          <div className="ug-modal-overlay" onClick={() => !submitting && setActivityModalEntry(null)}>
            <div className="ug-modal" onClick={e => e.stopPropagation()}>
              <div className="ug-modal-head">
                <h3>{activityModalEntry === 'new' ? 'Log procedure / case' : 'Edit log entry'}</h3>
                <FaTimes onClick={() => !submitting && setActivityModalEntry(null)} />
              </div>

              <label className="ug-form-label">Date</label>
              <input type="date" className="ug-form-input" value={activityForm.date} onChange={e => setActivityForm({ ...activityForm, date: e.target.value })} />

              <label className="ug-form-label">Procedure / Case</label>
              <input type="text" className="ug-form-input" placeholder="e.g. Normal vaginal delivery, IV cannulation" value={activityForm.procedureOrCase} onChange={e => setActivityForm({ ...activityForm, procedureOrCase: e.target.value })} />

              <label className="ug-form-label">Involvement level</label>
              <select className="ug-form-input" value={activityForm.involvementLevel} onChange={e => setActivityForm({ ...activityForm, involvementLevel: e.target.value })}>
                {INVOLVEMENT_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>

              <label className="ug-form-label">Case summary (optional)</label>
              <textarea className="ug-form-input ug-form-textarea" value={activityForm.caseSummary} onChange={e => setActivityForm({ ...activityForm, caseSummary: e.target.value })} />

              <label className="ug-form-label">Learning points (optional)</label>
              <textarea className="ug-form-input ug-form-textarea" value={activityForm.learningPoints} onChange={e => setActivityForm({ ...activityForm, learningPoints: e.target.value })} />

              <div className="ug-modal-actions">
                <button className="ug-btn-secondary" onClick={() => setActivityModalEntry(null)} disabled={submitting}>Cancel</button>
                <button className="ug-btn-primary" onClick={submitActivity} disabled={submitting}>{submitting ? 'Saving...' : 'Save entry'}</button>
              </div>
            </div>
          </div>
        )}
      </main>
    );
  }

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaUserMd /> Internship (CRMI) Logbook</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        NMC's Compulsory Rotating Medical Internship (CRMI) requires a minimum 12 months of supervised rotations after Final MBBS, completed within 2 years of passing, with a maximum of 15 days' leave across the year. Log each posting and the procedures/cases you observe, assist, or perform.
      </p>

      {postings.length > 0 && (
        <div className="ug-conf-stats">
          <div className="ug-conf-stat-box">
            <div className="ug-conf-stat-num">{postings.length}</div>
            <div className="ug-conf-stat-label">Postings</div>
          </div>
          <div className="ug-conf-stat-box">
            <div className="ug-conf-stat-num">{totalLeaveDays}<span style={{ fontSize: 13, fontWeight: 600, color: '#8792a6' }}>/15</span></div>
            <div className="ug-conf-stat-label">Leave Days</div>
          </div>
        </div>
      )}

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNewPosting}>
        <FaPlus /> Add posting
      </button>

      {postingsLoading ? (
        <div className="ug-due-empty">Loading postings...</div>
      ) : postings.length === 0 ? (
        <div className="ug-due-empty">No internship postings logged yet. Add your first departmental rotation to start logging procedures and cases.</div>
      ) : (
        <div className="ug-subject-grid">
          {postings.map(posting => (
            <div key={posting._id} className="ug-subject-card" onClick={() => openPosting(posting)}>
              <div className="ug-subject-card-head">
                <span className="ug-subject-code">{formatDate(posting.fromDate)}</span>
                <FaChevronRight />
              </div>
              <div className="ug-subject-name">{posting.department}</div>
              <div className="ug-progress-text">{posting.activityCount} {posting.activityCount === 1 ? 'entry' : 'entries'} logged</div>
              <div className="ug-reflection-card-actions" onClick={e => e.stopPropagation()}>
                <button className="ug-reflection-icon-btn" onClick={() => openEditPosting(posting)} title="Edit"><FaPencilAlt /></button>
                <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => removePosting(posting)} title="Delete"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {postingModalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setPostingModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>{postingModalEntry === 'new' ? 'Add posting' : 'Edit posting'}</h3>
              <FaTimes onClick={() => !submitting && setPostingModalEntry(null)} />
            </div>

            <label className="ug-form-label">Department</label>
            <select className="ug-form-input" value={postingForm.department} onChange={e => setPostingForm({ ...postingForm, department: e.target.value })}>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>

            <label className="ug-form-label">From date</label>
            <input type="date" className="ug-form-input" value={postingForm.fromDate} onChange={e => setPostingForm({ ...postingForm, fromDate: e.target.value })} />

            <label className="ug-form-label">To date</label>
            <input type="date" className="ug-form-input" value={postingForm.toDate} onChange={e => setPostingForm({ ...postingForm, toDate: e.target.value })} />

            <label className="ug-form-label">Unit / Ward / Hospital (optional)</label>
            <input type="text" className="ug-form-input" value={postingForm.unitWardHospital} onChange={e => setPostingForm({ ...postingForm, unitWardHospital: e.target.value })} />

            <label className="ug-form-label">Supervisor / Unit head (optional)</label>
            <input type="text" className="ug-form-input" value={postingForm.supervisorName} onChange={e => setPostingForm({ ...postingForm, supervisorName: e.target.value })} />

            <label className="ug-form-label">Leave days taken during this posting (optional)</label>
            <input type="number" min="0" className="ug-form-input" value={postingForm.leaveDaysTaken} onChange={e => setPostingForm({ ...postingForm, leaveDaysTaken: e.target.value })} />

            <label className="ug-form-label">Reflection (optional)</label>
            <textarea className="ug-form-input ug-form-textarea" value={postingForm.reflection} onChange={e => setPostingForm({ ...postingForm, reflection: e.target.value })} />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setPostingModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submitPosting} disabled={submitting}>{submitting ? 'Saving...' : 'Save posting'}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGInternship;
